import { useState, type ReactNode } from "react";
import { Clock, MapPin, Users, X } from "lucide-react";
import { ActionModal, Icon, Sheet, Spinner } from "@cntrl/ui";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { useConfirmHold, useDeleteShow, useRunOfShow, useShow, useShowMembers } from "../../app/hooks/useShows.js";
import { useFinancials } from "../../app/hooks/useMoney.js";
import { useDeleteBookingOffer, type BookingOfferDto } from "../../app/hooks/useBookingOffers.js";
import { pushToast } from "../../app/toasts.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { initials } from "../../shells/mobile/accountRoles.js";
import { longDate } from "../booking/lib/bookingRows.js";
import { slotRoleLabel } from "../booking/sharedShowFormat.js";
import { MobileShowEditor } from "../booking/MobileShowEditor.js";
import type { MobileEditorTab } from "../booking/ShowWizard.js";
import { formatDay, holdDaysLeft, offerDealLine } from "./rosterFormat.js";
import { NewOfferModal } from "./NewOfferModal.js";
import { SendToTeamModal } from "./SendToTeamModal.js";
import cal from "../booking/CalendarEventDetails.module.scss";
import styles from "./BookingShowSheet.module.scss";

/** What a booking agent tapped on the phone dashboard (ART-358): a show (a hold, one ready to confirm, or a
 *  confirmed date from Needs attention), or an offer still on the pre-hold shortlist. */
export type BookingSheetTarget =
  | { kind: "show"; showId: string; stage?: "hold" | "ready" }
  | { kind: "offer"; offer: BookingOfferDto };

type Stage = "offer" | "hold" | "ready" | "confirmed";

const STAGE_PILL: Record<Stage, { label: string; tone: string }> = {
  offer: { label: "Offer", tone: "itinerary" },
  hold: { label: "Waiting", tone: "invited" },
  ready: { label: "Ready", tone: "going" },
  confirmed: { label: "Confirmed", tone: "going" },
};

const STAGE_WORD: Record<Stage, string> = { offer: "Offer", hold: "Hold", ready: "Ready", confirmed: "Confirmed" };

function usd(cents: number | null | undefined): string | null {
  return cents == null ? null : `$${Math.round(cents / 100).toLocaleString()}`;
}

/**
 * The booking agent's READ-FIRST show sheet on the phone (ART-358), styled like the calendar's event sheet: the
 * stage, the date and place, the deal in three tappable rows, who is in, and the one or two things to do next.
 * Editing opens the phone editor (Basics · Lineup · Assets · Terms) in place of the sheet, and Save or Cancel
 * there comes back here. An OFFER is not a show yet, so its sheet carries only its terms, and its edit and send
 * are the offer form and Send to team the desktop board uses.
 */
export function BookingShowSheet({ target, onClose }: { target: BookingSheetTarget; onClose: () => void }) {
  return target.kind === "offer"
    ? <OfferSheet offer={target.offer} onClose={onClose} />
    : <ShowSheet showId={target.showId} stageHint={target.stage} onClose={onClose} />;
}

/** The sheet's frame: the calendar event sheet's own sizing and gutters. */
function Frame({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <Sheet title={title} onClose={onClose} scroll="inner" className={cal.sheet}>
      <div className={cal.frame} data-shell="mobile">{children}</div>
    </Sheet>
  );
}

function Head({ stage, title, sub, onClose }: { stage: Stage; title: string; sub: string; onClose: () => void }) {
  const pill = STAGE_PILL[stage];
  return (
    <header className={cal.head}>
      <div className={cal.headRow}>
        <span className={styles.square} aria-hidden />
        <span className={cal.kindLabel}>Show</span>
        <span className={cal.pill} data-tone={pill.tone}>{pill.label}</span>
        <button type="button" className={cal.close} onClick={onClose} aria-label="Close">
          <Icon icon={X} size={20} />
        </button>
      </div>
      <h2 className={cal.title}>{title}</h2>
      {sub && <p className={cal.date}>{sub}</p>}
    </header>
  );
}

function Strip({ children }: { children: ReactNode }) {
  return (
    <div className={cal.timeBar}>
      <span className={cal.timeFrame}>
        <Icon icon={Clock} size={16} />
        <span>{children}</span>
      </span>
    </div>
  );
}

function Section({ icon, label, children }: { icon: typeof MapPin; label: string; children: ReactNode }) {
  return (
    <section className={cal.section} aria-label={label}>
      <span className={cal.sectionIcon}><Icon icon={icon} size={16} /></span>
      <div className={cal.sectionBody}>{children}</div>
    </section>
  );
}

function DealRow({ title, sub, pill, onClick }: {
  title: string;
  sub: string;
  pill: { label: string; add?: boolean };
  onClick: () => void;
}) {
  return (
    <li>
      <button type="button" className={styles.dealRow} onClick={onClick}>
        <span className={styles.dealText}>
          <span className={styles.dealTitle}>{title}</span>
          <span className={styles.dealSub}>{sub}</span>
        </span>
        <span className={styles.dealPill} data-add={pill.add || undefined}>{pill.label}</span>
      </button>
    </li>
  );
}

function placeLine(...parts: (string | null | undefined)[]): string {
  return parts.map((p) => p?.trim()).filter(Boolean).join(", ");
}

// ─── A show: hold · ready · confirmed ──────────────────────────────────────────────────

type ShowView = { t: "sheet" } | { t: "edit"; tab: MobileEditorTab } | { t: "confirm" } | { t: "cancel" };

function ShowSheet({ showId, stageHint, onClose }: { showId: string; stageHint?: "hold" | "ready"; onClose: () => void }) {
  const { bandId } = useBandContext();
  const canFull = useCapability("money:full");
  const canPerShow = useCapability("money:per_show");
  const canMoney = canFull || canPerShow;
  const show = useShow(showId);
  const members = useShowMembers(showId);
  const finance = useFinancials(showId, canMoney);
  const ros = useRunOfShow(showId);
  const confirmHold = useConfirmHold();
  const deleteShow = useDeleteShow();
  const [view, setView] = useState<ShowView>({ t: "sheet" });

  const d = show.data?.data;
  const confirmed = d?.status === "confirmed" || d?.status === "settled";
  const stage: Stage = confirmed ? "confirmed" : stageHint ?? "hold";
  const venue = d?.venueName || "Untitled show";

  if (view.t === "edit") {
    return (
      <MobileShowEditor
        showId={showId}
        stageLabel={STAGE_WORD[stage]}
        initialTab={view.tab}
        onClose={() => setView({ t: "sheet" })}
        onSaved={() => {
          pushToast("Show saved.", "success");
          setView({ t: "sheet" });
        }}
      />
    );
  }

  if (show.isLoading || !d) {
    return (
      <Frame title="Show" onClose={onClose}>
        <div className={cal.loading}>
          {show.isError ? <p className={cal.error}>Couldn&apos;t load this show.</p> : <Spinner label="Loading show…" />}
        </div>
      </Frame>
    );
  }

  const fin = finance.data?.data;
  const terms = fin
    ? fin.doorSplit
      ? [fin.doorSplitPct != null ? `${fin.doorSplitPct}% door` : "Door share",
          usd(fin.guaranteeCents) ? `vs ${usd(fin.guaranteeCents)}` : null,
          fin.agentPct != null ? `${fin.agentPct}% agent` : null].filter(Boolean).join(" · ")
      : [usd(fin.guaranteeCents) ? `${usd(fin.guaranteeCents)} guarantee` : "No guarantee set",
          fin.agentPct != null ? `${fin.agentPct}% agent` : null].filter(Boolean).join(" · ")
    : canMoney ? "Loading…" : "Not visible to your role";

  // Our own act on the bill and its set length from the run of show (the stage row attributed to that slot).
  const own = (d.lineup ?? []).find((s) => s.friendBandId === (d.bandId ?? bandId));
  const setMin = own ? (ros.data?.items ?? []).find((i) => i.section === "stage" && i.slotId === own.slotId)?.durationMin : null;
  const billed = (d.lineup ?? []).length;
  const lineup = [own ? slotRoleLabel(own.slotRole) ?? "On the bill" : billed > 0 ? `${billed} on the bill` : "No lineup yet",
    setMin ? `${setMin} min set` : null].filter(Boolean).join(" · ");

  const rows = (members.data?.items ?? []).filter((m) => m.personId);
  const inCount = rows.filter((m) => m.rsvp === "in").length;
  const outCount = rows.filter((m) => m.rsvp === "out").length;
  const waiting = rows.length - inCount - outCount;
  const count = [inCount ? `${inCount} in` : null, waiting ? `${waiting} waiting` : null, outCount ? `${outCount} out` : null]
    .filter(Boolean).join(" · ");

  const days = holdDaysLeft(d.holdExpiresOn);
  const strip = stage === "ready"
    ? "All in · finish with the buyer"
    : stage === "hold"
      ? d.holdExpiresOn
        ? days != null && days < 0
          ? `Hold expired ${formatDay(d.holdExpiresOn)}`
          : `Hold expires ${formatDay(d.holdExpiresOn)} · ${days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`}`
        : "Hold · no expiry set"
      : null;

  return (
    <>
      <Frame title={venue} onClose={onClose}>
        <div className={cal.scroll}>
          <Head stage={stage} title={venue} sub={[d.date ? longDate(d.date) : null, placeLine(d.city, d.state) || null].filter(Boolean).join(" · ")} onClose={onClose} />
          {strip && <Strip>{strip}</Strip>}
          <div className={cal.sections}>
            {(d.venueName || d.city) && (
              <Section icon={MapPin} label="Location">
                {d.venueName && <span className={styles.venue}>{d.venueName}</span>}
                <span className={cal.sub}>{placeLine(d.city, d.state, d.country)}</span>
              </Section>
            )}
            <ul className={styles.dealBox} aria-label="The deal">
              <DealRow title="Terms" sub={terms} pill={{ label: "Edit" }} onClick={() => setView({ t: "edit", tab: "terms" })} />
              <DealRow title="Lineup" sub={lineup} pill={{ label: "Edit" }} onClick={() => setView({ t: "edit", tab: "lineup" })} />
              <DealRow
                title="Contract"
                sub={d.contractMediaId ? "Attached" : "Not attached"}
                pill={d.contractMediaId ? { label: "Edit" } : { label: "Add", add: true }}
                onClick={() => setView({ t: "edit", tab: "assets" })}
              />
            </ul>
            <Section icon={Users} label="Who's in">
              <span className={cal.countLine}>{rows.length === 0 ? "Nobody on this show yet" : count}</span>
              {rows.length > 0 && (
                <ul className={cal.people}>
                  {rows.map((m) => {
                    const status = m.rsvp === "in" ? "going" : m.rsvp === "out" ? "out" : "invited";
                    const name = m.displayName ?? "";
                    return (
                      <li key={m.personId} className={cal.chip} data-status={status} title={name}>
                        <span className={cal.ring}>
                          <PersonAvatar personId={m.personId!} avatarMediaId={m.avatarMediaId} initials={initials(name)} size="xs" />
                        </span>
                        <span className={cal.chipName}>{name.split(" ")[0] || "Someone"}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>
          </div>
        </div>
        <footer className={cal.actions}>
          <div className={cal.buttons}>
            <button type="button" className={`${cal.btnGhost} ${styles.cancel}`} onClick={() => setView({ t: "cancel" })}>
              Cancel show
            </button>
            {stage === "ready" ? (
              <button type="button" className={cal.btnPrimary} onClick={() => setView({ t: "confirm" })}>Confirm show</button>
            ) : (
              <button type="button" className={cal.btnPrimary} onClick={() => setView({ t: "edit", tab: "basics" })}>Edit</button>
            )}
          </div>
        </footer>
      </Frame>
      {view.t === "confirm" && (
        <ActionModal
          title="Confirm show?"
          onCancel={() => setView({ t: "sheet" })}
          cancelLabel="Not yet"
          confirmLabel="Confirm show"
          loading={confirmHold.isPending}
          onConfirm={() =>
            confirmHold.mutate(showId, {
              onSuccess: () => {
                pushToast(`${venue} confirmed. Everyone on it was told.`, "success");
                onClose();
              },
            })
          }
        >
          {`Confirm ${venue}? It goes out to everyone as a confirmed show.`}
        </ActionModal>
      )}
      {view.t === "cancel" && (
        <ActionModal
          title="Cancel show?"
          onCancel={() => setView({ t: "sheet" })}
          cancelLabel="Keep show"
          confirmLabel="Cancel show"
          confirmTone="danger"
          loading={deleteShow.isPending}
          onConfirm={() =>
            deleteShow.mutate(showId, {
              onSuccess: () => {
                pushToast(`${venue} canceled. Everyone attached was notified.`, "info");
                onClose();
              },
            })
          }
        >
          {`Cancel ${venue}? Everyone on this show will be told.`}
        </ActionModal>
      )}
    </>
  );
}

// ─── An offer on the shortlist ─────────────────────────────────────────────────────────

type OfferView = "sheet" | "edit" | "send" | "release";

function OfferSheet({ offer, onClose }: { offer: BookingOfferDto; onClose: () => void }) {
  const release = useDeleteBookingOffer();
  const [view, setView] = useState<OfferView>("sheet");
  if (view === "edit") return <NewOfferModal offer={offer} onClose={onClose} />;
  if (view === "send") return <SendToTeamModal offer={offer} onClose={onClose} />;

  const v = offer.request?.venue;
  const title = v?.name || offer.buyerVenue || "Untitled offer";
  const sent = offer.status === "requested";
  const reply = offer.goodThrough
    ? `Reply by ${new Date(`${offer.goodThrough.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" })} · ${formatDay(offer.goodThrough)}`
    : null;
  const deal = offerDealLine(offer.amountCents ?? null, offer.dealType ?? "guarantee", offer.doorSplitPct ?? null) ?? "No terms yet";
  const who = sent ? "Sent to the team · waiting on the artist"
    : offer.status === "passed" ? "Passed by the team"
    : "Not sent to the team yet";

  return (
    <>
      <Frame title={title} onClose={onClose}>
        <div className={cal.scroll}>
          <Head stage="offer" title={title} sub={[offer.date ? longDate(offer.date) : null, placeLine(v?.city, v?.state) || null].filter(Boolean).join(" · ")} onClose={onClose} />
          {reply && <Strip>{reply}</Strip>}
          <div className={cal.sections}>
            {(v?.name || offer.buyerVenue) && (
              <Section icon={MapPin} label="Location">
                <span className={styles.venue}>{v?.name || offer.buyerVenue}</span>
                {v && <span className={cal.sub}>{placeLine(v.city, v.state, v.country)}</span>}
              </Section>
            )}
            <ul className={styles.dealBox} aria-label="The deal">
              <DealRow title="Terms" sub={deal} pill={{ label: "Edit" }} onClick={() => setView("edit")} />
            </ul>
            <Section icon={Users} label="Who's in">
              <span className={cal.countLine}>{who}</span>
            </Section>
          </div>
        </div>
        <footer className={cal.actions}>
          <div className={cal.buttons}>
            <button type="button" className={`${cal.btnGhost} ${styles.cancel}`} onClick={() => setView("release")}>
              Cancel offer
            </button>
            {offer.status === "passed" ? (
              <button type="button" className={cal.btnPrimary} onClick={() => setView("edit")}>Edit</button>
            ) : (
              <button type="button" className={cal.btnPrimary} onClick={() => setView("send")}>
                {sent ? "Edit request" : "Send to team"}
              </button>
            )}
          </div>
        </footer>
      </Frame>
      {view === "release" && (
        <ActionModal
          title="Cancel this offer?"
          onCancel={() => setView("sheet")}
          cancelLabel="Keep"
          confirmLabel="Cancel offer"
          confirmTone="danger"
          loading={release.isPending}
          onConfirm={() =>
            release.mutate(offer.offerId!, {
              onSuccess: () => {
                pushToast(`${title} released.`, "success");
                onClose();
              },
            })
          }
        >
          {`Release ${title}${offer.date ? ` · ${formatDay(offer.date)}` : ""}? It comes off the shortlist.`}
        </ActionModal>
      )}
    </>
  );
}
